/* ════ SUPABASE LOAD + REALTIME ════════════════════════════ */
import { sb } from './client.js';
import { state } from '../state.js';
import { DEFAULT_RES, RES_COLS } from '../config.js';
import { $ } from '../dom.js';
import { showToast } from '../utils/toast.js';
import { buildGantt } from '../gantt/index.js';
import { renderIdeas, updateRainyBadge, applyInboxDefault } from '../ideas.js';
import { renderList } from '../listview.js';
import { renderHistory } from '../historyview.js';
import { renderCo2 } from '../co2board.js';
import { refreshCategories } from '../catmanager.js';
import { renderLeaveUI, onLeaveRequestChange, onLeaveAllowanceChange, onAbsenceChange } from '../leave.js';
import { refreshFilterResources, refreshFilterClients } from '../filters.js';
import { refreshAvatar } from '../auth.js';
import { displayName } from '../utils/name.js';
import { seasonalAfterLoad } from '../seasonal.js';
import { showDigestOnStartup } from '../digest.js';

// Append a row to the persistent activity history (best-effort).
export async function logActivity(action, entity, name, detail = '') {
  try { await sb.from('activity_log').insert({ actor: state.currentUser?.email || null, action, entity, name, detail }); }
  catch (e) { /* table may not exist yet */ }
}

// Email the invoice inbox when a task is flagged "ready to invoice".
// Best-effort: needs the Supabase 'notify-invoice' Edge Function deployed;
// silently no-ops until then.
export async function notifyReadyToInvoice(t) {
  try {
    const { error } = await sb.functions.invoke('notify-invoice', {
      body: {
        name: t.name, client: t.client || '', job: t.job || '',
        cost: Number(t.cost) || 0, hours: Number(t.hours) || 0,
        resource: state.resources.find(r => r.id === t.resource)?.name || '',
        by: state.currentUser?.email || '', start: t.start, end: t.end,
      },
    });
    if (!error) showToast('📧 Invoice team notified');
  } catch (e) { /* function not deployed — no-op */ }
}

// Resource ids whose login email is the signed-in user.
const myResourceIds = () => state.resources.filter(r => r.user_email && r.user_email === state.currentUser?.email).map(r => r.id);

// Best-effort user registry so everyone appears in the Inbox picker, even with
// an empty inbox / no tasks yet. Silently no-ops if app_users doesn't exist.
async function registerAndLoadUsers(email) {
  try {
    if (email) await sb.from('app_users').upsert({ email, name: displayName(email), last_seen: new Date().toISOString() });
    const { data, error } = await sb.from('app_users').select('email,name');
    if (!error && Array.isArray(data)) { state.users = data; if (state.currentView === 'ideas') renderIdeas(); }
  } catch (e) { /* table may not exist yet */ }
}

// Make sure the signed-in user has a planner lane. The app can't see Supabase
// Auth users until they log in, so we reconcile here on first load:
//  1. already linked to a resource → nothing to do;
//  2. an unlinked resource whose name matches (e.g. seeded "Robin") → link it;
//  3. otherwise create a fresh lane for them, linked to their login email.
async function ensureMyResource(email) {
  if (!email) return;
  if (state.resources.some(r => r.user_email && r.user_email === email)) return;
  const name = displayName(email) || email;
  // Try to adopt an existing same-named lane that has no login yet.
  const match = state.resources.find(r =>
    !r.user_email && r.id !== 'unassigned' && r.name.toLowerCase() === name.toLowerCase());
  if (match) {
    const { error } = await sb.from('resources').update({ user_email: email }).eq('id', match.id);
    if (!error) { match.user_email = email; refreshFilterResources(); refreshAvatar(); }
    return;
  }
  const id = (name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'user') + '-' + Date.now();
  const ini = name.split(/\s+/).map(w => w[0]).join('').substring(0, 2).toUpperCase() || '?';
  const col = RES_COLS[state.resources.length % RES_COLS.length];
  const row = { id, name, ini, col, sort: state.resources.length, user_email: email };
  const { error } = await sb.from('resources').insert(row);
  if (!error) {
    if (!state.resources.find(r => r.id === id)) state.resources.push(row);
    refreshFilterResources(); refreshAvatar(); buildGantt();
    showToast('Added you as a planner resource ✓');
  }
}

export function norm(t) {
  return {
    id: t.id, name: t.name, resource: t.resource, start: t.start, end: t.end,
    client: t.client || '', team: t.team || '',
    cat: t.cat || 'output', half: t.half || 'none', job: t.job || '',
    designer: t.designer || '', consultant: t.consultant || '',
    info: t.info || '', link: t.link || '',
    importance: t.importance || 'normal', created_by: t.created_by || '',
    hours: t.hours ?? null, cost: t.cost ?? null,
    tags: Array.isArray(t.tags) ? t.tags : [], locked: !!t.locked, completed: !!t.completed, private: !!t.private,
    ready_invoice: !!t.ready_invoice, invoiced: !!t.invoiced,
    subtasks: Array.isArray(t.subtasks) ? t.subtasks : [],
    // Who/when, for "While you were away" (updated_by is stamped by the database).
    created_at: t.created_at, updated_at: t.updated_at, updated_by: t.updated_by || '',
  };
}
export function normTrip(t) {
  return {
    id: t.id, name: t.name || '', resource: t.resource || 'unassigned',
    start: t.start, end: t.end, mode: t.mode || 'other', zone: t.zone || 'UK',
    co2: t.co2 ?? 0, distance_km: t.distance_km ?? null,
    client: t.client || '', job: t.job || '', info: t.info || '',
    source_key: t.source_key || '', created_by: t.created_by || '', created_at: t.created_at,
  };
}
export function normIdea(i) {
  return {
    id: i.id, name: i.name, description: i.description || '', cat: i.cat || 'output',
    resource: i.resource || '', days: i.days || null, due: i.due || null,
    importance: i.importance || 'normal', created_by: i.created_by || '',
    tags: Array.isArray(i.tags) ? i.tags : [], sort: i.sort ?? null, locked: !!i.locked, private: !!i.private,
    mentions: Array.isArray(i.mentions) ? i.mentions : [],
    subtasks: Array.isArray(i.subtasks) ? i.subtasks : [], created_at: i.created_at,
    updated_at: i.updated_at, updated_by: i.updated_by || '',
  };
}

// Rebuild whichever task view is active.
function refreshTaskViews() {
  buildGantt();
  refreshFilterClients();
  if (state.currentView === 'list') renderList();
}

export function setSyncDot(s) { const d = $('sync-dot'); if (d) d.className = 'sync-dot ' + s; }

const wait = ms => new Promise(r => setTimeout(r, ms));

export async function loadAll(attempt = 0) {
  setSyncDot('busy');
  const email = state.currentUser?.email || '';
  try {
    const [tr, tt, ti, ta, tp, ss, ig, cc, la, lr, ab] = await Promise.all([
      sb.from('resources').select('*').order('sort'),
      sb.from('tasks').select('*').order('start'),
      // All inboxes load; the Inbox view filters to the selected user.
      sb.from('ideas').select('*').order('created_at', { ascending: false }),
      sb.from('activity_log').select('*').order('at', { ascending: false }).limit(250),
      sb.from('trips').select('*').order('start'),
      sb.from('app_settings').select('*'),
      sb.from('trip_ignores').select('source_key'),
      sb.from('categories').select('*'),
      // Leave: the database only returns your own rows (or everyone's, for the approver).
      sb.from('leave_allowances').select('*'),
      sb.from('leave_requests').select('*').order('start'),
      // Absence records: HR sees all; everyone else only their own in-lieu / adjustment rows.
      sb.from('absence_records').select('*').order('start'),
    ]);
    // 42P01 = table missing (fresh project); tolerate that, retry anything else.
    const fatal = [tr.error, tt.error, ti.error, ta.error, tp.error, ss.error, ig.error, cc.error, la.error, lr.error, ab.error].find(e => e && e.code !== '42P01');
    if (fatal) throw fatal;
    if (!tr.data || tr.data.length === 0) { await sb.from('resources').insert(DEFAULT_RES); state.resources = DEFAULT_RES; }
    else state.resources = tr.data;
    state.tasks = (tt.data || []).map(norm);
    state.ideas = (ti.data || []).map(normIdea);
    state.trips = (tp.data || []).map(normTrip);
    state.settings = Object.fromEntries((ss.data || []).map(r => [r.key, r.value]));
    state.tripIgnores = new Set((ig.data || []).map(r => r.source_key));
    state.categories = cc.data || [];
    state.leaveAllowances = la.data || [];
    state.leaveRequests = lr.data || [];
    state.absences = ab.data || [];
    // HR notes passphrase versions (HR only; others get none). Never fatal.
    try { const hk = await sb.from('hr_keys').select('*').order('created_at'); state.hrKeys = hk.error ? [] : (hk.data || []); } catch (e) { state.hrKeys = []; }
    state.activity = ta.data || [];
    applyInboxDefault();
    refreshFilterResources(); refreshFilterClients(); refreshAvatar();
    setSyncDot('ok'); seasonalAfterLoad(); buildGantt(); updateRainyBadge(); refreshCategories(); renderLeaveUI(); subscribeRT();
    showDigestOnStartup();
    await ensureMyResource(email);
    registerAndLoadUsers(email);
  } catch (e) {
    // The first load can race the freshly-issued auth token / a cold backend.
    // Retry with backoff before surfacing anything.
    if (attempt < 4) { await wait(500 * (attempt + 1)); return loadAll(attempt + 1); }
    setSyncDot('err'); showToast('Load error: ' + e.message);
    state.resources = state.resources.length ? state.resources : DEFAULT_RES;
    buildGantt(); updateRainyBadge();
  }
}

export function subscribeRT() {
  sb.channel('grantt-v3')
    .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks' }, p => {
      const { eventType: ev, new: n, old: o } = p;
      // Notify me when a task is newly assigned to my resource.
      const mine = myResourceIds();
      if (mine.length && (ev === 'INSERT' || ev === 'UPDATE') && mine.includes(n.resource)) {
        const prev = state.tasks.find(t => t.id === n.id);
        const justAssigned = ev === 'INSERT' || (prev && prev.resource !== n.resource);
        if (justAssigned && n.created_by !== state.currentUser?.email) showToast('📌 Assigned to you: ' + n.name);
      }
      if (ev === 'INSERT' && !state.tasks.find(t => t.id === n.id)) state.tasks.push(norm(n));
      else if (ev === 'UPDATE') state.tasks = state.tasks.map(t => t.id === n.id ? norm(n) : t);
      else if (ev === 'DELETE') state.tasks = state.tasks.filter(t => t.id !== o.id);
      refreshTaskViews();
    })
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'activity_log' }, p => {
      state.activity.unshift(p.new); if (state.activity.length > 300) state.activity.pop();
      if (state.currentView === 'history') renderHistory();
    })
    .on('postgres_changes', { event: '*', schema: 'public', table: 'resources' }, p => {
      const { eventType: ev, new: n, old: o } = p;
      if (ev === 'INSERT' && !state.resources.find(r => r.id === n.id)) state.resources.push(n);
      else if (ev === 'UPDATE') state.resources = state.resources.map(r => r.id === n.id ? n : r);
      else if (ev === 'DELETE') state.resources = state.resources.filter(r => r.id !== o.id);
      refreshFilterResources(); refreshAvatar(); refreshTaskViews();
    })
    .on('postgres_changes', { event: '*', schema: 'public', table: 'ideas' }, p => {
      const { eventType: ev, new: n, old: o } = p;
      const me = state.currentUser?.email;
      const prev = ev === 'UPDATE' ? state.ideas.find(i => i.id === n.id) : null;
      if (ev === 'INSERT' && !state.ideas.find(i => i.id === n.id)) state.ideas.unshift(normIdea(n));
      else if (ev === 'UPDATE') state.ideas = state.ideas.map(i => i.id === n.id ? normIdea(n) : i);
      else if (ev === 'DELETE') state.ideas = state.ideas.filter(i => i.id !== o.id);
      // Nudge when someone flags me to review a card (newly, not by me).
      if (me && (ev === 'INSERT' || ev === 'UPDATE')) {
        const nowM = Array.isArray(n.mentions) ? n.mentions : [];
        const prevM = prev ? (prev.mentions || []) : [];
        if (nowM.includes(me) && !prevM.includes(me) && n.created_by !== me) showToast('🔔 Flagged to review: ' + n.name);
      }
      updateRainyBadge();
      if (state.currentView === 'ideas') renderIdeas();
    })
    .on('postgres_changes', { event: '*', schema: 'public', table: 'trips' }, p => {
      const { eventType: ev, new: n, old: o } = p;
      if (ev === 'INSERT' && !state.trips.find(t => t.id === n.id)) state.trips.push(normTrip(n));
      else if (ev === 'UPDATE') state.trips = state.trips.map(t => t.id === n.id ? normTrip(n) : t);
      else if (ev === 'DELETE') state.trips = state.trips.filter(t => t.id !== o.id);
      if (state.currentView === 'co2') renderCo2();
    })
    .on('postgres_changes', { event: '*', schema: 'public', table: 'app_settings' }, p => {
      const n = p.new; if (n && n.key) state.settings[n.key] = n.value;
      if (state.currentView === 'co2') renderCo2();
    })
    .on('postgres_changes', { event: '*', schema: 'public', table: 'trip_ignores' }, p => {
      const { eventType: ev, new: n, old: o } = p;
      if (ev === 'INSERT' && n?.source_key) state.tripIgnores.add(n.source_key);
      else if (ev === 'DELETE' && o?.source_key) state.tripIgnores.delete(o.source_key);
    })
    .on('postgres_changes', { event: '*', schema: 'public', table: 'categories' }, p => {
      const { eventType: ev, new: n, old: o } = p;
      if (ev === 'INSERT' && !state.categories.find(c => c.id === n.id)) state.categories.push(n);
      else if (ev === 'UPDATE') state.categories = state.categories.map(c => c.id === n.id ? n : c);
      else if (ev === 'DELETE') state.categories = state.categories.filter(c => c.id !== o.id);
      refreshCategories();
    })
    .on('postgres_changes', { event: '*', schema: 'public', table: 'leave_requests' }, onLeaveRequestChange)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'leave_allowances' }, onLeaveAllowanceChange)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'absence_records' }, onAbsenceChange)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'hr_keys' }, async () => {
      const hk = await sb.from('hr_keys').select('*').order('created_at');
      if (!hk.error) { state.hrKeys = hk.data || []; renderLeaveUI(); }
    })
    .subscribe(s => { if (s === 'SUBSCRIBED') setSyncDot('ok'); if (s === 'CLOSED' || s === 'CHANNEL_ERROR') setSyncDot('err'); });
}

export async function dbUpdate(id, changes) {
  setSyncDot('busy');
  const { error } = await sb.from('tasks').update(changes).eq('id', id);
  if (error) { setSyncDot('err'); showToast('Sync error: ' + error.message); }
  else { setSyncDot('ok'); const t = state.tasks.find(x => x.id === id); logActivity('moved', 'task', t?.name || '', changes.start ? '→ ' + changes.start : ''); }
}
